import { useCallback, useRef } from "react";
import { AiMappingCostItemInput } from "generated/graphql";
import { useSyncAiMappingCostsToMap } from "./hooks";
import {
  configToSyncItem,
  loadAiCostConfigs,
  saveAiMeasurementFeatureIds,
  toSyncKind,
  toSyncValueKind,
} from "./aiMappingStorage";
import { AiMappingSourceSyncItem } from "./aiMappingControllerTypes";

interface UseAiMappingAssignmentSyncOptions {
  projectMapId: string | null;
  existingMeasurementIds?: string[];
  aiMeasurementIds: string[];
  unmountedRef: React.MutableRefObject<boolean>;
  handleError: (error: unknown, options?: { inline?: boolean }) => boolean;
  setAiMeasurementIds: React.Dispatch<React.SetStateAction<string[]>>;
}

export function useAiMappingAssignmentSync({
  projectMapId,
  existingMeasurementIds,
  aiMeasurementIds,
  unmountedRef,
  handleError,
  setAiMeasurementIds,
}: UseAiMappingAssignmentSyncOptions) {
  const [syncCostsAction] = useSyncAiMappingCostsToMap();
  const assignmentSyncQueueRef = useRef<Promise<void>>(Promise.resolve());

  const syncAiSourceToAreas = useCallback(
    async (areaIds: string[], item: AiMappingSourceSyncItem) => {
      const sync = async () => {
        let lastFeatureIds: string[] = [];
        const fallbackItem: AiMappingCostItemInput = {
          productId: item.productId,
          kind: toSyncKind(item.kind) ?? undefined,
          category: item.category ?? "",
          categories: item.categories,
          valueKind: toSyncValueKind(item.valueKind) ?? undefined,
          linearFeetPerUnit: item.linearFeetPerUnit,
        };

        for (const areaId of areaIds) {
          const savedItems: AiMappingCostItemInput[] = projectMapId
            ? loadAiCostConfigs(projectMapId)
                .filter(
                  (config) =>
                    config.areaId === areaId && config.productId && !config.additionalCostId
                )
                .map(configToSyncItem)
            : [];

          const result = await syncCostsAction({
            variables: {
              input: {
                areaId,
                items: savedItems.length ? savedItems : [fallbackItem],
              },
            },
          });
          if (!result.success) {
            if (!unmountedRef.current) handleError(result);
            return;
          }
          lastFeatureIds = Array.from(
            new Set(
              (result.data?.syncAiMappingCostsToMap?.measurements ?? [])
                .map((measurement) => measurement.geojsonFeatureId)
                .filter((id): id is string => !!id)
            )
          );
        }

        if (lastFeatureIds.length && !unmountedRef.current) {
          const previous = new Set(aiMeasurementIds);
          const preExisting = new Set(existingMeasurementIds ?? []);
          const next = lastFeatureIds.filter((id) => previous.has(id) || !preExisting.has(id));
          setAiMeasurementIds(next);
          if (projectMapId) saveAiMeasurementFeatureIds(projectMapId, next);
        }
      };

      const queued = assignmentSyncQueueRef.current.then(sync, sync);
      assignmentSyncQueueRef.current = queued.catch(() => undefined);
      await queued;
    },
    [
      syncCostsAction,
      projectMapId,
      existingMeasurementIds,
      aiMeasurementIds,
      handleError,
      setAiMeasurementIds,
      assignmentSyncQueueRef,
      unmountedRef,
    ]
  );

  return { syncAiSourceToAreas };
}
